import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useCompanies } from '@/hooks/useCompanies'
import { DataTable } from '@/components/ui/data-table'
import { SearchInput } from '@/components/ui/search-input'
import { Pagination } from '@/components/ui/pagination'
import { StatusBadge } from '@/components/ui/status-badge'
import { Button } from '@/components/ui/button'
import { maskCnpj, maskPhone } from '@/components/form/masks'
import type { Company } from '@/types/company'
import type { Column } from '@/components/ui/data-table'

type ActiveFilter = 'all' | 'active' | 'inactive'

const FILTER_LABELS: Record<ActiveFilter, string> = {
  all: 'Todas',
  active: 'Ativas',
  inactive: 'Inativas',
}

const COLUMNS: Column<Company>[] = [
  { key: 'tradeName', header: 'Nome fantasia' },
  {
    key: 'cnpj',
    header: 'CNPJ',
    render: (row) => maskCnpj(row.cnpj),
  },
  {
    key: 'city',
    header: 'Cidade/UF',
    render: (row) =>
      row.city && row.state
        ? `${row.city} / ${row.state}`
        : row.city ?? '—',
  },
  {
    key: 'phone',
    header: 'Telefone',
    className: 'hidden lg:table-cell',
    render: (row) => (row.phone ? maskPhone(row.phone) : '—'),
  },
  {
    key: 'active',
    header: 'Status',
    render: (row) => <StatusBadge active={row.active} />,
  },
]

export function CompanyListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [page, setPage] = useState(1)

  const activeParam =
    activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined

  const { data, isLoading } = useCompanies({ search, active: activeParam, page })

  function handleSearch(v: string) {
    setSearch(v)
    setPage(1)
  }

  function handleFilter(f: ActiveFilter) {
    setActiveFilter(f)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Empresas</h1>
          <p className="text-sm text-muted-foreground">
            Gerenciamento de tomadores de frete
          </p>
        </div>
        <Button onClick={() => navigate('/companies/new')}>
          <Plus size={16} />
          Nova empresa
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar por nome, razão social ou CNPJ…"
          className="flex-1"
        />
        <div className="flex overflow-hidden rounded-md border border-input text-sm">
          {(Object.keys(FILTER_LABELS) as ActiveFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => handleFilter(f)}
              className={`px-3 py-1.5 transition-colors ${
                activeFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={(row) => navigate(`/companies/${row.id}`)}
        isLoading={isLoading}
        emptyTitle="Nenhuma empresa encontrada"
        emptyDescription="Comece cadastrando a primeira empresa."
        emptyAction={
          <Button onClick={() => navigate('/companies/new')}>
            <Plus size={16} />
            Nova empresa
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}
    </div>
  )
}
